import { useEffect, useState, useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { getLabellingInfo } from "@/api";
import ReactMarkdown from "react-markdown";
import BrandLogo from "@/components/BrandLogo";
import Container from "@/components/Container";
import Page from "@/components/Page";
import ceMarkImage from "@/assets/symbols/ce1912.png";
import udiImage from "@/assets/symbols/udi.png";
import eifuImage from "@/assets/symbols/eifu.png";
import manufacturerImage from "@/assets/symbols/manufacturer.png";
import medicalDeviceImage from "@/assets/symbols/md.png";
import styles from "./BMSLabelling.module.scss";

function LabellingSection({ section }: { section: BMSLabellingSection }) {
  return (
    <div className={styles.section}>
      {section.header && <h2>{section.header}</h2>}
      {section.body && <ReactMarkdown>{section.body}</ReactMarkdown>}
    </div>
  );
}

function ModuleDetails({
  module,
  t,
}: {
  module: BMSModule;
  t: (key: string) => string;
}) {
  const manufacturerMarkdown = module.manufacturer
    ? module.manufacturer.replace(/\\n/g, "  \n")
    : "";

  return (
    <div>
      {module.description?.map((section, i) => (
        <LabellingSection key={i} section={section} />
      ))}

      {module.classification && (
        <div className={styles.symbolRow}>
          <img
            src={medicalDeviceImage}
            alt="Medical Device"
            className={styles.symbolSmall}
          />
          <div className={styles.symbolContent}>
            <span>
              {t("bms.labels.name")} {module.description[0]?.header}
            </span>
            <span>
              {t("bms.labels.classification")} {module.classification}
            </span>
            {module.version && (
              <span>
                {t("bms.labels.version")} {module.version}
              </span>
            )}
          </div>
        </div>
      )}

      {module.manufacturer && (
        <div className={styles.symbolRow}>
          <img
            src={manufacturerImage}
            alt="Manufacturer"
            className={styles.symbolSmall}
          />
          <div
            className={styles.symbolContent}
            style={{ "white-space": "pre" } as React.CSSProperties}
          >
            <ReactMarkdown>{manufacturerMarkdown}</ReactMarkdown>
          </div>
        </div>
      )}

      {module.udi && (
        <div className={styles.symbolRow}>
          <img src={udiImage} alt="UDI" className={styles.symbolSmall} />
          <span className={styles.symbolContent}>{module.udi}</span>
        </div>
      )}

      {module.udi && (
        <div className={styles.symbolRow}>
          <img src={eifuImage} alt="eIFU" className={styles.symbolSmall} />
          {module.eifu ? (
            <a
              href={module.eifu}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.eifuLink}
            >
              {module.eifu}
            </a>
          ) : (
            "Detta gränssnitt innehåller inga interaktioner med medicinteknik"
          )}
        </div>
      )}

      {module.ceMarkIdentifier && (
        <div className={`${styles.symbolRow} mt-30`}>
          <img src={ceMarkImage} alt="CE Mark" className={styles.symbolCe} />
        </div>
      )}
    </div>
  );
}

export default function BMSLabelling() {
  const { t } = useTranslation();
  const [labellingData, setLabellingData] = useState<BMSLabellingInfo>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    setIsLoading(true);

    getLabellingInfo()
      .then((res) => {
        if (!res.ok) {
          throw new Error();
        }
        return res.json();
      })
      .then(setLabellingData)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, []);

  const groupedModules = useMemo(() => {
    if (!labellingData?.modules)
      return {} as Record<BMSModuleType, BMSModule[]>;

    return labellingData.modules.reduce<Record<string, BMSModule[]>>(
      (acc, mod) => {
        const type = mod.moduleType;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(mod);
        return acc;
      },
      {},
    );
  }, [labellingData?.modules]);

  return (
    <Container>
      <BrandLogo />

      <Page>
        <div className={styles.wrapper}>
          {isLoading && !labellingData && !error ? (
            <div className={styles.skeleton}>
              {[...Array(4)].map((_, i) => (
                <div key={i} className={styles.skeletonItem} />
              ))}
            </div>
          ) : error ? (
            <div className={styles.error}>{t("bms.error_message")}</div>
          ) : (
            <>
              {/* Generic labelling sections */}
              {labellingData?.labellingInformation?.map((section, i) => (
                <LabellingSection key={i} section={section} />
              ))}

              {/* Client section */}
              {labellingData?.client && (
                <div>
                  <h2>{t("bms.client.heading")}</h2>
                  <p>{t("bms.client.description")}</p>

                  <div className={styles.metadataField}>
                    <span className={styles.metadataLabel}>
                      {t("bms.labels.interface")}
                    </span>
                    <span className={styles.metadataValue}>
                      {labellingData.client.system}
                    </span>
                  </div>

                  {labellingData.client.labellingInformation?.map(
                    (section, i) => (
                      <LabellingSection key={i} section={section} />
                    ),
                  )}
                </div>
              )}

              {/* BMS version */}
              {labellingData?.version && (
                <div className={styles.metadataField}>
                  <span className={styles.metadataLabel}>
                    {t("bms.labels.version")}
                  </span>
                  <span className={styles.metadataValue}>
                    {labellingData.version}
                  </span>
                </div>
              )}

              {/* Modules section */}
              {Object.keys(groupedModules).length > 0 && (
                <div>
                  {(
                    Object.entries(groupedModules) as [
                      BMSModuleType,
                      BMSModule[],
                    ][]
                  ).map(([moduleType, modules]) => (
                    <div key={moduleType} className={styles.moduleTypeSection}>
                      <h2 className={styles.moduleTypeHeading}>
                        {t(`bms.modules.${moduleType.toLowerCase()}.heading`)}
                      </h2>
                      <p className={styles.moduleTypeDescription}>
                        {t(
                          `bms.modules.${moduleType.toLowerCase()}.description`,
                        )}
                      </p>

                      {modules.map((mod, i) => (
                        <Fragment key={i}>
                          <ModuleDetails module={mod} t={t} />
                        </Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Compatibility warning */}
              {labellingData?.compatibility === "NOT_SUPPORTED" && (
                <div className={styles.compatibilityWarning}>
                  {t("bms.compatibility_warning")}
                </div>
              )}

              {/* Footer */}
              {labellingData?.version && (
                <div className={styles.footer}>{labellingData.version}</div>
              )}
            </>
          )}
        </div>
      </Page>
    </Container>
  );
}
